import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useUpdateActividadPrograma } from "@/hooks/actividadesProgramas/useUpdateActividadPrograma";
import {
  editActividadProgramaSchema,
  type EditActividadProgramaInput,
} from "@/lib/validaciones/editActividadPrograma.valicacion";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useState } from "react";
import { useProgramaById } from "@/hooks/programas/useProgramas";
import { useCurrentUser } from "@/features/login/hooks/useCurrentUser";
import { ActividadProgramaArchivoField } from "@/components/forms/actividades-programas/ActividadProgramaArchivoField";
import { uploadActividadProgramaArchivo } from "@/components/forms/actividades-programas/uploadActividadProgramaArchivo";

export function EditActividadProgramaForm({
  data,
  close,
}: {
  data: EditActividadProgramaInput;
  close: () => void;
}) {
  const { user } = useCurrentUser();
  const isSuperAdmin = user?.rol?.nombre === "SUPER_ADMIN";

  const [archivoLimpioFile, setArchivoLimpioFile] = useState<File | null>(null);
  const [archivoWordFile, setArchivoWordFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const form = useForm<EditActividadProgramaInput>({
    resolver: zodResolver(editActividadProgramaSchema),
    defaultValues: {
      ...data,
      rol: data.rol ?? "",
    },
  });

  const { data: programaSeleccionado } = useProgramaById(
    form.watch("idPrograma"),
  );

  const { mutate } = useUpdateActividadPrograma();

  const onSubmit = async (values: EditActividadProgramaInput) => {
    setIsSubmitting(true);
    try {
      let archivoLimpio = values.archivoLimpio ?? null;
      let archivoWord = values.archivoWord ?? null;
    if (programaSeleccionado?.cerebro) {
      if (archivoLimpioFile) {
        archivoLimpio = await uploadActividadProgramaArchivo(
          archivoLimpioFile,
          programaSeleccionado as { cerebro: string },
          values.nombre,
          "pdf",
        );
      }
      if (archivoWordFile) {
        archivoWord = await uploadActividadProgramaArchivo(
          archivoWordFile,
          programaSeleccionado as { cerebro: string },
          values.nombre,
          "word",
        );
      }
    }
    mutate(
      {
        ...values,
        archivoLimpio,
        archivoWord,
      },
      {
        onSuccess: () => {
          close();
          setArchivoLimpioFile(null);
          setArchivoWordFile(null);
        },
        onSettled: () => {
          setIsSubmitting(false);
        },
      },
    );
    } catch {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4 sm:space-y-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
          <FormField
            control={form.control}
            name="nombre"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Nombre</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Campo "Rol del Usuario" solo visible para SUPER_ADMIN */}
          {isSuperAdmin && (
            <FormField
            control={form.control}
            name="rol"
            render={({ field }) => (
              <FormItem className="md:col-span-2">
                <FormLabel>Rol del Usuario</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                    }}
                    placeholder="Describe el rol o función que desempeñará el usuario en esta actividad..."
                    className="min-h-[80px] max-h-[200px] resize-y overflow-y-auto"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          )}

          <ActividadProgramaArchivoField
            label="Archivo limpio (*.pdf)"
            accept=".pdf"
            inputId="archivo-limpio-upload-edit"
            selectedFile={archivoLimpioFile}
            onFileChange={setArchivoLimpioFile}
            currentNombre={
              !archivoLimpioFile ? data.archivoLimpio?.nombre : undefined
            }
          />
          <ActividadProgramaArchivoField
            label="Archivo Word (*.doc, *.docx)"
            accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            inputId="archivo-word-upload-edit"
            selectedFile={archivoWordFile}
            onFileChange={setArchivoWordFile}
            currentNombre={
              !archivoWordFile ? data.archivoWord?.nombre : undefined
            }
          />

          {["fechaInicio", "fechaFin"].map((campo) => (
            <FormField
              key={campo}
              control={form.control}
              name={campo as "fechaInicio" | "fechaFin"}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {campo === "fechaInicio"
                      ? "Fecha de inicio"
                      : "Fecha de fin"}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      value={
                        field.value
                          ? new Date(field.value).toISOString().split("T")[0]
                          : ""
                      }
                      onChange={(e) => field.onChange(new Date(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ))}
        </div>
        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full h-10 sm:h-11 text-sm sm:text-base"
        >
          {isSubmitting ? "Guardando..." : "Guardar Cambios"}
        </Button>
      </form>
    </Form>
  );
}
